
import {
  defineComponent, PropType, computed, ref, onMounted, Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import variables from '@/components/template-builder/utils/variables-list';
import {
  loadSenderAddresses,
  loadSenderNames,
  searchValuesForAutocompletion,
} from '@/composables/autoCompletionValuesMedia';
import getShopsDomains from '@/composables/shop/ShopsSendDomains';
import { OperatorType, ShopsSendDomains } from '@/types/generated-types/graphql';

export interface MessageData {
  template_message_subject?: Record<string, string>;
  template_message_preview_text?: Record<string, string>;
  template_message_expname?: Record<string, string>;
  template_message_expmail?: Record<string, string>;
  template_message_replytomail?: Record<string, string>;
}

export default defineComponent({
  name: 'MessageConfig',

  components: {
    FieldErrors,
    LocalizedTextInput,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: Object as PropType<MessageData>,
      required: true,
    },

    media: {
      type: String,
      required: true,
    },

    activeTemplateId: {
      type: Number,
      default: 0,
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'go-to-domain-page'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Local reactive copy of the data
    const localData = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    // Helper to check if a field has errors
    const hasFieldError = (fieldName: string): boolean => {
      // formValidation is a Vuelidate v$ object, access .value first
      const validation = props.formValidation?.value || props.formValidation;
      if (!validation) return false;

      const fieldNames = fieldName.split('.');
      let current: any = validation;
      for (const name of fieldNames) {
        if (!current || !current[name]) return false;
        current = current[name];
      }
      return current?.$errors?.length > 0 || current?.$silentErrors?.length > 0;
    };

    // Autocompletion data
    const autoCompletionData: Ref<any> = ref({
      senderNames: [] as string[],
      senderAddresses: [] as string[],
      domainsOfShop: [] as ShopsSendDomains[],
    });

    // Computed visibility flags based on media type and data presence
    const showSubject = computed(() => (
      props.media === 'email' && localData.value.template_message_subject !== undefined
    ));

    const showPreviewText = computed(() => (
      props.media === 'email' && localData.value.template_message_preview_text !== undefined
    ));

    const showExpName = computed(() => (
      ['email', 'sms', 'whatsapp'].includes(props.media)
      && localData.value.template_message_expname !== undefined
    ));

    const showExpMail = computed(() => (
      props.media === 'email' && localData.value.template_message_expmail !== undefined
    ));

    const showReplyToMail = computed(() => (
      props.media === 'email' && localData.value.template_message_replytomail !== undefined
    ));

    // Variables list for text inputs
    const variablesList = computed(() => variables.allVariables);

    // Search functions for autocompletion
    const searchSenderName = (query: string) => (
      searchValuesForAutocompletion('senderName', query, autoCompletionData.value)
    );

    const searchSenderAddress = (query: string) => (
      searchValuesForAutocompletion('senderAddress', query, autoCompletionData.value)
    );

    // Load autocompletion data on mount
    onMounted(async () => {
      autoCompletionData.value.senderNames = await loadSenderNames(props.media);
      if (props.media === 'email') {
        autoCompletionData.value.senderAddresses = await loadSenderAddresses();
        autoCompletionData.value.domainsOfShop = await getShopsDomains(0, [{
          field: 'domain_verified',
          operator: OperatorType.Equals,
          value: true,
        }]);
      }
    });

    return {
      t,
      localData,
      showSubject,
      showPreviewText,
      showExpName,
      showExpMail,
      showReplyToMail,
      variablesList,
      searchSenderName,
      searchSenderAddress,
      hasFieldError,
    };
  },
});
